/**
 * AskMojiThread — chat-style input + render display + feedback row.
 *
 * One thread at a time. After each render the user can tap More-like /
 * Less-like / Forget / Remember / Wrong / quieter / louder.
 */

import { useState } from 'react';
import { Pin, ThumbsDown, ThumbsUp, Trash2, TriangleAlert, Volume1, Volume2 } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { api, type FeedbackAction, type PalProfileResponse, type RenderResponse } from '../cloud/api';
import type { UseAskMojiThreadResult } from './hooks/useAskMojiThread';

export function AskMojiThread({
    pal,
    onPalUpdated,
    thread,
    query,
    submitting,
    error,
    setQuery,
    ask,
}: {
    pal: PalProfileResponse;
    onPalUpdated: (p: PalProfileResponse) => void;
    thread: UseAskMojiThreadResult['thread'];
    query: string;
    submitting: boolean;
    error: string | null;
    setQuery: UseAskMojiThreadResult['setQuery'];
    ask: () => Promise<void>;
}) {
    return (
        <div className="card">
            <h3 className="font-semibold mb-2">Ask {pal.pal_name}</h3>

            <div className="space-y-3 mb-3 max-h-96 overflow-y-auto">
                {thread.map(({ user, render }, i) => (
                    <Turn key={i} userText={user} render={render} onPalUpdated={onPalUpdated} />
                ))}
                {thread.length === 0 && (
                    <p className="muted text-sm">
                        Ask anything — a place to eat, help drafting an email,
                        or just chat. {pal.pal_name} learns from every conversation.
                    </p>
                )}
            </div>

            <div className="flex gap-2">
                <input
                    className="flex-1 p-2 rounded-lg border border-line"
                    placeholder={`Ask ${pal.pal_name}…`}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) void ask(); }}
                    disabled={submitting}
                />
                <button
                    className="btn-primary"
                    onClick={() => { void ask(); }}
                    disabled={submitting || !query.trim()}
                >{submitting ? '…' : 'Ask'}</button>
            </div>

            {error && (
                <p className="text-danger text-sm mt-2" role="alert">{error}</p>
            )}
        </div>
    );
}

function Turn({ userText, render, onPalUpdated }: {
    userText: string;
    render: RenderResponse;
    onPalUpdated: (p: PalProfileResponse) => void;
}) {
    return (
        <div>
            <div className="text-sm text-right">
                <span className="inline-block bg-brand/15 px-3 py-1.5 rounded-2xl rounded-tr-sm">
                    {userText}
                </span>
            </div>
            <RenderBubble render={render} onPalUpdated={onPalUpdated} />
        </div>
    );
}

function RenderBubble({ render, onPalUpdated }: {
    render: RenderResponse;
    onPalUpdated: (p: PalProfileResponse) => void;
}) {
    const [feedbackSent, setFeedbackSent] = useState<FeedbackAction | null>(null);
    const [showWhy, setShowWhy] = useState(false);

    async function send(action: FeedbackAction) {
        if (feedbackSent) return;
        setFeedbackSent(action);
        try {
            await api.feedback({ render_id: render.render_id, action });
            // Tone-adjusting actions immediately mutate the Vibe Profile;
            // refresh from server so the home card stays in sync.
            if (action === 'tone_quieter' || action === 'tone_louder') {
                const fresh = await api.palMe();
                if (fresh) onPalUpdated(fresh);
            }
        } catch {
            setFeedbackSent(null);   // allow retry
        }
    }

    return (
        <div className="text-sm mt-1">
            <div className="bg-surface-raised px-3 py-2 rounded-2xl rounded-tl-sm border border-line whitespace-pre-wrap">
                {render.text}
            </div>
            <div className="muted text-xs mt-1 flex items-center gap-2 flex-wrap">
                <span>{render.intent}</span>
                {render.evidence_count > 0 && (
                    <span>· {render.evidence_count} echo{render.evidence_count === 1 ? '' : 's'}</span>
                )}
                {render.resonance && (
                    <button
                        onClick={() => setShowWhy((v) => !v)}
                        className="underline"
                    >
                        {Math.round(render.resonance.score * 100)}% match · why?
                    </button>
                )}
            </div>
            {showWhy && render.resonance && <ResonanceBreakdown breakdown={render.resonance.breakdown} />}
            <div className="flex gap-1 mt-1.5 flex-wrap">
                {(['more_like', 'less_like', 'remember', 'forget', 'tone_quieter', 'tone_louder'] as FeedbackAction[]).map((a) => (
                    <button
                        key={a}
                        onClick={() => send(a)}
                        disabled={!!feedbackSent}
                        className={`text-xs px-2 py-1 rounded-full border transition ${
                            feedbackSent === a
                                ? 'bg-success/25 border-success'
                                : 'border-line hover:border-brand/50'
                        }`}
                        aria-label={`feedback: ${a}`}
                    >
                        <FeedbackLabel action={a} />
                    </button>
                ))}
            </div>
        </div>
    );
}

function ResonanceBreakdown({ breakdown }: { breakdown: Record<string, number> }) {
    const entries = Object.entries(breakdown).sort((a, b) => b[1] - a[1]);
    return (
        <div className="bg-surface-raised/60 px-3 py-2 rounded-lg border border-line text-xs mt-1">
            <div className="muted mb-1">Resonance breakdown</div>
            {entries.map(([k, v]) => (
                <div key={k} className="flex justify-between">
                    <span>{k.replace(/_/g, ' ')}</span>
                    <span className="font-mono">{v.toFixed(3)}</span>
                </div>
            ))}
        </div>
    );
}

const FEEDBACK_META: Record<FeedbackAction, { label: string; Icon: LucideIcon }> = {
    more_like: { label: 'more', Icon: ThumbsUp },
    less_like: { label: 'less', Icon: ThumbsDown },
    remember: { label: 'remember', Icon: Pin },
    forget: { label: 'forget', Icon: Trash2 },
    tone_quieter: { label: 'quieter', Icon: Volume1 },
    tone_louder: { label: 'louder', Icon: Volume2 },
    wrong: { label: 'wrong', Icon: TriangleAlert },
};

function FeedbackLabel({ action }: { action: FeedbackAction }) {
    const { label, Icon } = FEEDBACK_META[action];
    return (
        <span className="inline-flex items-center gap-1">
            <Icon className="h-3.5 w-3.5" aria-hidden strokeWidth={2.2} />
            <span>{label}</span>
        </span>
    );
}
